/**
 * Tracking (`/tracking`) — v1 [mm] 14-10-2026, v2 16-10-2026 (Jordy).
 *
 * Volgorde = de vraag van Jordy eerst:
 *   1. Fan activity & MM effect: per model unieke fans + inkomende berichten
 *      (uur/dag/week/maand), tijdlijn met MM-lijnen, voor/na per MM + MM-timing.
 *   2. Posts — de fanreis: posts -> nieuwe volgers -> schreven -> nieuwe
 *      chatters (per model, per dag, per post); daarna likes/comments.
 *   3. Beste tijden (heatmaps).
 * Alles is tijd-correlatie. F2F pas vanaf de inname-fix; FC-engagement 35/30/30
 * blijft gefilterd (besluit Jordy 15-10).
 *
 * Endpoints (alleen owner/admin): /api/admin/tracking/{activity,timeline,
 * mm-batches,journey,posts,inbound}
 * Zie src/services/mm_tracking.py voor de regels.
 */
import { useMemo, useState } from 'react'
import { ErrorNote, Kpi, Loading, Panel, Plat, Seg, Table, Tag } from '../components/ui'
import { ModelNaam } from '../components/ModelNaam'
import { ModelFilter, useModelFilter } from '../components/ModelFilter'
import { useModellen } from '../lib/modellen'
import { useApi } from '../lib/useApi'
import { useMijnZone } from '../lib/tijdzone'
import { alsZone, zoneLabel } from '../lib/amsterdam'
import { num } from '../lib/format'
import { Chart } from '../components/charts/Chart'
import { Modal } from '../components/Modal'
import { KLEUREN, basis, lijnReeks, mengKleur, xAs, yAs } from '../components/charts/theme'

interface PostItem {
  post_id: number; model_slug: string; platform: string; live_at: string
  kind: string | null; paid: boolean
  likes: number | null; comments: number | null; views: number | null; bookmarks: number | null
  unreliable: boolean
  measured_at: string | null; measured_after_hours: number | null
  journey: { window_hours: number; new_followers: number; new_chatters: number } | null
}
interface Slot { day: number; hour: number; avg: number; posts: number }
interface Posts {
  posts: number; measured: number; avg_likes: number | null; avg_comments: number | null
  unreliable: number; unreliable_reason: string | null
  items: PostItem[]; items_truncated: boolean
  heatmap: { avg: (number | null)[][]; posts: number[][] }
  best_slots: Slot[]; min_posts_per_slot: number
}
interface Inbound {
  matrix: number[][]; total: number
  per_model: { model_slug: string; platform: string; messages: number }[]
  f2f_reliable_from: string; f2f_excluded: number
}
type Eenheid = 'hour' | 'day' | 'week' | 'month'
interface Gem { messages: number | null; fans: number | null }
interface ActivityRow {
  model_slug: string; platform: string; messages: number; fans: number
  from: string; to: string; days: number; avg: Record<Eenheid, Gem>
  followers: { count: number; fetched_at: string; age_hours: number } | null
}
interface Activity { models: ActivityRow[]; f2f_reliable_from: string; followers_stale_after_hours: number }
interface Timeline {
  model_slug: string; platform: string; unit: Eenheid; from: string; to: string
  buckets: { label: string; messages: number; fans: number }[]
  mm: { id: number; sent_at: string; bucket: string; kind: string; text: string; recipients: number }[]
}
interface Effect {
  before: { messages: number; fans: number }; after: { messages: number; fans: number }
  lift_messages_pct: number | null; lift_fans_pct: number | null
  after_complete: boolean; after_hours: number; before_reliable: boolean; after_reliable: boolean
  other_mms: number
}
interface JourneyDay { day: string; posts: number; new_followers: number; followers_wrote_48h: number; new_chatters: number }
interface JourneyRow {
  model_slug: string; platform: string | null; posts: number
  new_followers: number; followers_wrote_48h: number; followers_wrote: number; followers_still_open: number
  new_chatters: number; new_chatters_were_new_followers: number; days: JourneyDay[]
}
interface Journey { models: JourneyRow[]; follower_write_window_hours: number }
interface Batch {
  id: number; sent_at: string; model_slug: string; platform: string; kind: string; text: string
  has_media: boolean; paid: boolean; price: number | null
  recipients: number; sent: number; failed: number; status: string
  reached: number; replied: number; reply_rate: number | null; median_reply_minutes: number | null
  replies_within: Record<string, number>
  certainty: 'exact' | 'estimate'; f2f_intake_incomplete: boolean
  timing: { wrote_before_mm: number; first_after_mm: number }
  effect: Record<string, Effect>
}
interface Batches {
  batches: Batch[]; batches_truncated: boolean; reply_window_hours: number; f2f_reliable_from: string
  effect_window_hours: number
}

const PLATFORMS = [
  { value: 'all', label: 'All' },
  { value: 'fancentro', label: 'FanCentro' },
  { value: 'f2f', label: 'F2F' },
]
const RANGES = [
  { value: '7', label: '7d' }, { value: '14', label: '14d' },
  { value: '30', label: '30d' }, { value: '90', label: '90d' },
]
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const UNITS: { value: Eenheid; label: string }[] = [
  { value: 'hour', label: 'Hour' }, { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' }, { value: 'month', label: 'Month' },
]
const PER: Record<Eenheid, string> = { hour: 'hour', day: 'day', week: 'week', month: 'month' }
const MM_VENSTERS = [{ value: '4', label: '4h' }, { value: '8', label: '8h' }, { value: '24', label: '24h' }]

/** Sorteerkolommen van de fan-activiteitstabel (16-10, Jordy). */
type SorteerSleutel = 'model' | 'fans' | 'msgs' | 'day' | 'week' | 'month' | 'followers' | 'totfans' | 'totmsgs' | 'days'
function sorteerWaarde(r: ActivityRow, k: SorteerSleutel, e: Eenheid, naam: (slug: string) => string): number | string | null {
  switch (k) {
    case 'model': return naam(r.model_slug).toLowerCase()
    case 'fans': return r.avg[e].fans
    case 'msgs': return r.avg[e].messages
    case 'day': return r.avg.day.messages
    case 'week': return r.avg.week.messages
    case 'month': return r.avg.month.messages
    case 'followers': return r.followers?.count ?? null
    case 'totfans': return r.fans
    case 'totmsgs': return r.messages
    case 'days': return r.days
  }
}

/** Leeftijd als "5h" of "3d". */
function leeftijd(uur: number): string {
  return uur < 48 ? `${Math.round(uur)}h` : `${Math.round(uur / 24)}d`
}

/** "12 → 18 (+50%)", of de reden waarom er geen stijging berekend is. */
function VoorNa({ e, wat, venster }: { e: Effect | undefined; wat: 'messages' | 'fans'; venster: string }) {
  if (!e) return <span className="dim">—</span>
  const lift = wat === 'messages' ? e.lift_messages_pct : e.lift_fans_pct
  const reden = !e.after_complete ? `after window still running (${e.after_hours}h of ${venster})`
    : !e.before_reliable || !e.after_reliable ? 'F2F window starts before the intake fix'
    : (wat === 'messages' ? e.before.messages : e.before.fans) === 0 ? 'no activity before: no baseline' : null
  return (
    <span style={{ whiteSpace: 'nowrap' }} title={reden ?? 'Same window before vs after the MM (time correlation)'}>
      <span className="mono">{num(e.before[wat])} → {num(e.after[wat])}</span>{' '}
      {lift == null
        ? <span className="dim">{reden ? '·' : ''}</span>
        : <Tag kind={lift > 0 ? 'ok' : lift < 0 ? 'bad' : 'mut'}>{lift > 0 ? '+' : ''}{lift}%</Tag>}
    </span>
  )
}

function Deel({ n, van }: { n: number; van: number }) {
  return <>{num(n)}{van ? <span className="mut2" style={{ fontSize: 11 }}> ({Math.round((100 * n) / van)}%)</span> : null}</>
}

/** Lijngrafiek met een verticale stippellijn op elk MM-moment. */
function Tijdlijn({ data }: { data: Timeline }) {
  const labels = data.buckets.map((b) => b.label)
  const mmEmmers = [...new Set(data.mm.map((m) => m.bucket))]
  const reeksBerichten = {
    ...lijnReeks('Fan messages', data.buckets.map((b) => b.messages), KLEUREN.acc, { gevuld: false }),
    markLine: {
      symbol: 'none', silent: false,
      label: { formatter: 'MM', color: KLEUREN.warn, fontSize: 10, position: 'end' },
      lineStyle: { color: KLEUREN.warn, type: 'dashed', width: 1.5 },
      data: mmEmmers.map((b) => ({ xAxis: b })),
    },
  }
  return (
    <Chart
      height={280}
      empty={!labels.length}
      option={{
        ...basis({ legenda: true }),
        xAxis: xAs(labels),
        yAxis: yAs((v: number) => String(v)),
        series: [reeksBerichten,
          { ...lijnReeks('Unique fans', data.buckets.map((b) => b.fans), KLEUREN.teal, { gevuld: false }),
            lineStyle: { width: 2, color: KLEUREN.teal, shadowBlur: 10, shadowColor: mengKleur(KLEUREN.teal, 0.5) } }],
      }}
    />
  )
}

/** Kalenderdag (YYYY-MM-DD) in Amsterdam, N dagen terug; de server rekent ook in Amsterdam-dagen. */
function amsterdamDag(dagenTerug: number): string {
  const d = new Date(Date.now() - dagenTerug * 86_400_000)
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Amsterdam' }).format(d)
}

function waarde(v: number | null | undefined): JSX.Element | string {
  return v == null ? <span className="dim">—</span> : num(v)
}

/** Dag x uur-raster; lege cellen (null) blijven grijs. */
function Heat({ matrix, tip, kleur }: {
  matrix: (number | null)[][]
  tip: (d: number, h: number, v: number | null) => string
  kleur: string
}) {
  const max = Math.max(1, ...matrix.flat().map((v) => v ?? 0))
  return (
    <div className="tblwrap">
      <div className="heat">
        <div />
        {Array.from({ length: 24 }).map((_, h) => (
          <div className="hh" key={h}>{String(h).padStart(2, '0')}</div>
        ))}
        {matrix.map((row, d) => (
          <>
            <div className="hd" key={`d${d}`}>{DAYS[d]}</div>
            {row.map((v, h) => (
              <div
                className="c"
                key={`${d}-${h}`}
                title={tip(d, h, v)}
                style={{ background: v == null ? 'rgba(127,127,127,.08)'
                  : `rgba(${kleur},${(0.06 + (v / max) * 0.9).toFixed(2)})` }}
              />
            ))}
          </>
        ))}
      </div>
    </div>
  )
}

export default function Tracking() {
  const [dagen, setDagen] = useState('7')
  const [van, setVan] = useState('')
  const [tot, setTot] = useState('')
  const [platform, setPlatform] = useState('all')
  const [eenheid, setEenheid] = useState<Eenheid>('day')
  const [gekozen, setGekozen] = useState<string | null>(null)
  const [sorteer, setSorteer] = useState<{ k: SorteerSleutel; op: boolean }>({ k: 'msgs', op: false })
  const [mmVenster, setMmVenster] = useState('4')
  const [voorbeeld, setVoorbeeld] = useState<Batch | null>(null)
  const kiesSortering = (k: SorteerSleutel) => setSorteer((v) => (v.k === k ? { k, op: !v.op } : { k, op: k === 'model' }))
  const { modelVan } = useModellen()
  const filter = useModelFilter()
  const zone = useMijnZone()

  const venster = useMemo(() => {
    if (van && tot) return { van, tot }
    return { van: amsterdamDag(Number(dagen) - 1), tot: amsterdamDag(0) }
  }, [dagen, van, tot])
  const q = `from_date=${venster.van}&to_date=${venster.tot}`
    + (platform === 'all' ? '' : `&platform=${platform}`) + filter.param
  const tz = `&tz=${encodeURIComponent(zone)}`

  const activity = useApi<Activity>(`/api/admin/tracking/activity?${q}${tz}`, [q, tz])
  const batches = useApi<Batches>(`/api/admin/tracking/mm-batches?${q}&window_hours=${mmVenster}`, [q, mmVenster])
  const journey = useApi<Journey>(`/api/admin/tracking/journey?${q}${tz}`, [q, tz])
  const posts = useApi<Posts>(`/api/admin/tracking/posts?${q}${tz}`, [q, tz])
  const inbound = useApi<Inbound>(`/api/admin/tracking/inbound?${q}${tz}`, [q, tz])

  // Het model voor de tijdlijn: de keuze, anders het drukste model.
  const modellen = activity.data?.models ?? []
  const gesorteerd = useMemo(() => {
    const naam = (slug: string) => modelVan(slug)?.naam ?? slug
    const lijst = [...modellen]
    lijst.sort((a, b) => {
      const x = sorteerWaarde(a, sorteer.k, eenheid, naam)
      const y = sorteerWaarde(b, sorteer.k, eenheid, naam)
      // Ontbrekend (bv. geen volgerdata) altijd onderaan, in beide richtingen.
      if (x == null && y == null) return 0
      if (x == null) return 1
      if (y == null) return -1
      const v = typeof x === 'string' ? x.localeCompare(String(y)) : (x as number) - (y as number)
      return sorteer.op ? v : -v
    })
    return lijst
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modellen, sorteer, eenheid])
  const tijdlijnModel = gekozen && modellen.some((m) => m.model_slug === gekozen)
    ? gekozen : (modellen[0]?.model_slug ?? null)
  const tijdlijn = useApi<Timeline>(
    tijdlijnModel
      ? `/api/admin/tracking/timeline?from_date=${venster.van}&to_date=${venster.tot}&slug=${encodeURIComponent(tijdlijnModel)}&unit=${eenheid}${tz}`
      : null,
    [tijdlijnModel, venster.van, venster.tot, eenheid, tz])
  const reisModel = (journey.data?.models ?? []).find((m) => m.model_slug === tijdlijnModel)
    ?? journey.data?.models[0] ?? null

  const f2fVanaf = activity.data?.f2f_reliable_from ?? inbound.data?.f2f_reliable_from ?? null
  const zoneTekst = zoneLabel(zone)
  const per = PER[eenheid]

  return (
    <>
      <div className="filters">
        <Seg options={PLATFORMS} value={platform} onChange={setPlatform} platform />
        {RANGES.map((r) => (
          <button key={r.value} type="button"
                  className={`btn sm${dagen === r.value && !van && !tot ? '' : ' ghost'}`}
                  onClick={() => { setDagen(r.value); setVan(''); setTot('') }}>{r.label}</button>
        ))}
        <input className="inp" type="date" value={van} title="From"
               onChange={(e) => setVan(e.target.value)} style={{ width: 138 }} />
        <input className="inp" type="date" value={tot} title="To"
               onChange={(e) => setTot(e.target.value)} style={{ width: 138 }} />
        <ModelFilter filter={filter} />
      </div>

      <div className="card" style={{ marginBottom: 12, fontSize: 13 }}>
        <b>Everything here is a time correlation, not a proven cause.</b>{' '}
        <span className="mut2">
          Times and calendar buckets are {zoneTekst} time. Forward-only: only data that is reliable now.
        </span>
      </div>
      {f2fVanaf ? (
        <div className="card" style={{ marginBottom: 16, fontSize: 13, borderLeft: '3px solid var(--warn, #f59e0b)' }}>
          <b>F2F is limited.</b>{' '}
          <span className="mut2">
            F2F fan activity counts only from {alsZone(f2fVanaf, zone)} (intake fix); before that the F2F intake
            missed messages{inbound.data?.f2f_excluded ? ` (${num(inbound.data.f2f_excluded)} older F2F messages left out)` : ''}.
            F2F MM links are an estimate. <b>FanCentro messages are reliable.</b>
          </span>
        </div>
      ) : null}

      <h2 style={{ margin: '8px 0 12px' }}>Fan activity &amp; MM effect</h2>

      <Panel title="Fan activity per model"
             desc={`Unique fans who wrote and inbound fan messages, on average per ${per}. Click a column header to sort (again to reverse); click a row to see its timeline. "Per day/week/month" sort on messages.`}
             right={<Seg options={UNITS} value={eenheid} onChange={(v) => setEenheid(v as Eenheid)} />}
             flush>
        {activity.loading ? <Loading /> : activity.error ? <ErrorNote error={activity.error} /> : (
          <div className="tblwrap">
            <table className="tbl" data-testid="tracking-activity">
              <thead>
                <tr>
                  {([
                    ['model', 'Model', false], [null, 'Platform', false],
                    ['fans', `Unique fans / ${per}`, true], ['msgs', `Fan messages / ${per}`, true],
                    ['day', 'Per day (fans · msgs)', true], ['week', 'Per week', true], ['month', 'Per month', true],
                    ['followers', 'Total followers', true], ['totfans', 'Total fans', true],
                    ['totmsgs', 'Total msgs', true], ['days', 'Days counted', true],
                  ] as [SorteerSleutel | null, string, boolean][]).map(([k, label, rechts]) => (
                    <th key={label} className={rechts ? 'num' : undefined}
                        style={k ? { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' } : undefined}
                        aria-sort={k && sorteer.k === k ? (sorteer.op ? 'ascending' : 'descending') : undefined}
                        onClick={k ? () => kiesSortering(k) : undefined}>
                      {label}{k && sorteer.k === k ? (sorteer.op ? ' ▲' : ' ▼') : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {gesorteerd.length === 0 ? (
                  <tr><td colSpan={11} className="empty">No fan messages in this window</td></tr>
                ) : gesorteerd.map((r) => {
                  const oud = r.followers && r.followers.age_hours > (activity.data?.followers_stale_after_hours ?? 24)
                  return (
                    <tr key={`${r.model_slug}-${r.platform}`} onClick={() => setGekozen(r.model_slug)}
                        style={{ cursor: 'pointer', background: r.model_slug === tijdlijnModel ? 'rgba(168,85,247,.10)' : undefined }}>
                      <td><ModelNaam slug={r.model_slug} /></td>
                      <td><Plat value={r.platform} /></td>
                      <td className="num mono"><b>{waarde(r.avg[eenheid].fans)}</b></td>
                      <td className="num mono"><b>{waarde(r.avg[eenheid].messages)}</b></td>
                      <td className="num mono">{waarde(r.avg.day.fans)} · {waarde(r.avg.day.messages)}</td>
                      <td className="num mono">{waarde(r.avg.week.fans)} · {waarde(r.avg.week.messages)}</td>
                      <td className="num mono">{waarde(r.avg.month.fans)} · {waarde(r.avg.month.messages)}</td>
                      <td className="num mono" style={{ whiteSpace: 'nowrap' }}
                          title={r.followers ? `Follower Tracking, last fetched ${alsZone(r.followers.fetched_at, zone)}` : 'No follower data for this model (Follower Tracking has no row)'}>
                        {r.followers == null ? <span className="dim">—</span>
                          : oud ? <span className="dim">{num(r.followers.count)} <Tag kind="warn">{leeftijd(r.followers.age_hours)} old</Tag></span>
                          : num(r.followers.count)}
                      </td>
                      <td className="num mono">{num(r.fans)}</td>
                      <td className="num mono">{num(r.messages)}</td>
                      <td className="num mono" title={`${alsZone(r.from, zone)} – ${alsZone(r.to, zone)}`}>{r.days}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel title={`Timeline${tijdlijnModel ? '' : ' — no model'}`}
             desc={<>Fan messages and unique fans per {per}. Dashed line = a mass message to this model went out. Does the line go up <i>after</i> it?</>}
             right={
               <div className="row" style={{ gap: 8 }}>
                 <select className="select" aria-label="Timeline model" value={tijdlijnModel ?? ''}
                         onChange={(e) => setGekozen(e.target.value)}>
                   {modellen.map((m) => <option key={m.model_slug} value={m.model_slug}>{m.model_slug}</option>)}
                 </select>
                 <Seg options={UNITS} value={eenheid} onChange={(v) => setEenheid(v as Eenheid)} />
               </div>
             }>
        {!tijdlijnModel ? <div className="empty" style={{ padding: 16 }}>No model with fan activity in this window.</div>
          : tijdlijn.loading ? <Loading /> : tijdlijn.error ? <ErrorNote error={tijdlijn.error} /> : tijdlijn.data ? (
            <>
              <div className="row" style={{ gap: 8, marginBottom: 8, fontSize: 12 }}>
                <ModelNaam slug={tijdlijn.data.model_slug} /> <Plat value={tijdlijn.data.platform} />
                <span className="mut2">{num(tijdlijn.data.mm.length)} mass message(s) in this window</span>
              </div>
              <Tijdlijn data={tijdlijn.data} />
              {tijdlijn.data.mm.length ? (
                <div className="mut2" style={{ fontSize: 12, marginTop: 8 }}>
                  {tijdlijn.data.mm.map((x) => (
                    <div key={x.id}><b>{alsZone(x.sent_at, zone)}</b> · {x.kind} · {num(x.recipients)} recipients · <span title={x.text}>{x.text || '—'}</span></div>
                  ))}
                </div>
              ) : null}
            </>
          ) : null}
      </Panel>

      {batches.loading ? <Loading /> : batches.error ? <ErrorNote error={batches.error} /> : batches.data ? (
        <Panel title="MM effect: before vs after"
               desc={<>All fan activity of the model in the same window before vs after each mass message ({batches.data.effect_window_hours}h — short, so 3 MMs a day don't overlap), plus who wrote back within {batches.data.reply_window_hours}h: fans who <b>already wrote before</b> the MM vs fans whose <b>first message came after</b> it. The 7-day columns are the <b>model trend</b>, not the effect of one MM. Click a row to read the full message. A rise next to an MM is a time correlation, not proof.{batches.data.batches_truncated ? ' Showing the newest 200 batches.' : ''}</>}
               right={<Seg options={MM_VENSTERS} value={mmVenster} onChange={setMmVenster} />}
               flush>
          <Table cols={[
            { key: 'sent', label: 'Sent' }, { key: 'model', label: 'Model' }, { key: 'plat', label: 'Platform' },
            { key: 'reach', label: 'Reached', num: true },
            { key: 'f48', label: `Fans ${batches.data.effect_window_hours}h before → after`, num: true },
            { key: 'm48', label: `Msgs ${batches.data.effect_window_hours}h before → after`, num: true },
            { key: 'tim', label: `Wrote back ≤${batches.data.reply_window_hours}h: already active · first after MM`, num: true },
            { key: 'f7', label: 'Model trend 7d: fans (not per MM)', num: true },
            { key: 'm7', label: 'Model trend 7d: msgs (not per MM)', num: true },
            { key: 'zek', label: '' },
          ]} emptyText="No mass messages in this window">
            {batches.data.batches.map((b) => {
              const terug = b.timing.wrote_before_mm + b.timing.first_after_mm
              return (
                <tr key={b.id} onClick={() => setVoorbeeld(b)} style={{ cursor: 'pointer' }} title="Click to read the full message">
                  <td className="mono" style={{ whiteSpace: 'nowrap' }}>{alsZone(b.sent_at, zone)} <span className="mut2" style={{ fontSize: 11 }}>{zone}</span></td>
                  <td><ModelNaam slug={b.model_slug} /></td>
                  <td><Plat value={b.platform} /></td>
                  <td className="num mono">{num(b.reached)}</td>
                  <td className="num"><VoorNa e={b.effect.window} wat="fans" venster={`${batches.data!.effect_window_hours}h`} /></td>
                  <td className="num"><VoorNa e={b.effect.window} wat="messages" venster={`${batches.data!.effect_window_hours}h`} /></td>
                  <td className="num mono" style={{ whiteSpace: 'nowrap' }}>
                    {terug ? <><Deel n={b.timing.wrote_before_mm} van={terug} /> · <Deel n={b.timing.first_after_mm} van={terug} /></>
                      : <span className="dim">nobody</span>}
                  </td>
                  <td className="num mut2"><VoorNa e={b.effect.trend7d} wat="fans" venster="7d" /></td>
                  <td className="num mut2"><VoorNa e={b.effect.trend7d} wat="messages" venster="7d" /></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {b.certainty === 'exact'
                      ? <Tag kind="ok" title="FanCentro: MM copy linked by platform id">exact</Tag>
                      : <Tag kind="warn" title="F2F: MM copy linked by text and time">estimate</Tag>}
                    {b.f2f_intake_incomplete
                      ? <> <Tag kind="bad" title="Sent before the F2F intake fix: activity is undercounted">incomplete</Tag></> : null}
                    {(b.effect.window?.other_mms ?? 0) > 0
                      ? <> <Tag kind="warn" title={`Other mass messages to this model within ${batches.data!.effect_window_hours}h: their effects overlap`}>+{b.effect.window.other_mms} MM nearby</Tag></> : null}
                  </td>
                </tr>
              )
            })}
          </Table>
        </Panel>
      ) : null}

      {voorbeeld ? (
        <Modal title="Mass message" width={560} onClose={() => setVoorbeeld(null)}>
          <div className="col" style={{ gap: 10 }} data-testid="tracking-mm-preview">
            <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <ModelNaam slug={voorbeeld.model_slug} /> <Plat value={voorbeeld.platform} />
              <span className="mono">{alsZone(voorbeeld.sent_at, zone)}</span> <span className="mut2">{zone}</span>
            </div>
            <div className="mut2" style={{ fontSize: 12 }}>
              {voorbeeld.kind}{voorbeeld.has_media ? ' · with media' : ''}{voorbeeld.paid ? ` · PPV${voorbeeld.price ? ` ${voorbeeld.price}` : ''}` : ''}
              {' · '}{num(voorbeeld.recipients)} recipients · {num(voorbeeld.reached)} reached
            </div>
            <div className="card" style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.5 }}>
              {voorbeeld.text || <span className="dim">(no text)</span>}
            </div>
          </div>
        </Modal>
      ) : null}

      <h2 style={{ margin: '24px 0 12px' }}>Posts — the fan journey</h2>
      {journey.loading ? <Loading /> : journey.error ? <ErrorNote error={journey.error} /> : journey.data ? (
        <>
          <Panel title="Fan journey per model"
                 desc={<>Posts went live → <b>new followers</b> (recognised by the platform's automatic welcome message in their chat — an approximation) → how many of them <b>wrote within {journey.data.follower_write_window_hours}h</b> → <b>new chatters</b> (fans whose first message ever came in this window). Time correlation, not proven cause. Click a row for the day-by-day view.</>}
                 flush>
            <Table cols={[
              { key: 'model', label: 'Model' }, { key: 'plat', label: 'Platform' },
              { key: 'p', label: 'Posts live', num: true }, { key: 'v', label: 'New followers', num: true },
              { key: 'w', label: `Wrote ≤${journey.data.follower_write_window_hours}h`, num: true },
              { key: 'o', label: 'Still open', num: true }, { key: 'c', label: 'New chatters', num: true },
              { key: 'cv', label: 'of which new followers', num: true },
            ]} emptyText="No posts, followers or new chatters in this window">
              {journey.data.models.map((r) => (
                <tr key={r.model_slug} onClick={() => setGekozen(r.model_slug)}
                    style={{ cursor: 'pointer', background: r.model_slug === reisModel?.model_slug ? 'rgba(168,85,247,.10)' : undefined }}>
                  <td><ModelNaam slug={r.model_slug} /></td>
                  <td>{r.platform ? <Plat value={r.platform} /> : null}</td>
                  <td className="num mono">{num(r.posts)}</td>
                  <td className="num mono"><b>{num(r.new_followers)}</b></td>
                  <td className="num mono"><Deel n={r.followers_wrote_48h} van={r.new_followers} /></td>
                  <td className="num mono" title="Followed less than 48h ago and has not written yet">{num(r.followers_still_open)}</td>
                  <td className="num mono"><b>{num(r.new_chatters)}</b></td>
                  <td className="num mono"><Deel n={r.new_chatters_were_new_followers} van={r.new_chatters} /></td>
                </tr>
              ))}
            </Table>
          </Panel>
          {reisModel && reisModel.days.length ? (
            <Panel title="Fan journey by day" flush
                   desc={<><ModelNaam slug={reisModel.model_slug} /> — posts, new followers and new chatters per day.</>}>
              <Table cols={[
                { key: 'd', label: 'Day' }, { key: 'p', label: 'Posts live', num: true },
                { key: 'v', label: 'New followers', num: true }, { key: 'w', label: 'Wrote ≤48h', num: true },
                { key: 'c', label: 'New chatters', num: true },
              ]}>
                {reisModel.days.map((d) => (
                  <tr key={d.day}>
                    <td className="mono">{d.day}</td>
                    <td className="num mono">{num(d.posts)}</td>
                    <td className="num mono">{num(d.new_followers)}</td>
                    <td className="num mono"><Deel n={d.followers_wrote_48h} van={d.new_followers} /></td>
                    <td className="num mono">{num(d.new_chatters)}</td>
                  </tr>
                ))}
              </Table>
            </Panel>
          ) : null}
        </>
      ) : null}

      {posts.loading ? <Loading /> : posts.error ? <ErrorNote error={posts.error} /> : posts.data ? (
        <>
          {posts.data.unreliable ? (
            <div className="card" style={{ marginBottom: 16, fontSize: 13, borderLeft: '3px solid var(--bad, #ef4444)' }}>
              <b>FanCentro engagement is not reliable yet.</b>{' '}
              <span className="mut2">
                {num(posts.data.unreliable)} readings are left out: {posts.data.unreliable_reason}.
                Averages, heatmap and best times use only the other readings.
              </span>
            </div>
          ) : null}
          <div className="grid kpis">
            <Kpi label="Posts went live" value={num(posts.data.posts)} icon="⤴" tone="acc"
                 meta="go-live time inside the window" />
            <Kpi label="Measured" value={num(posts.data.measured)} icon="✓" tone="ok"
                 meta={posts.data.unreliable ? `usable readings · ${num(posts.data.unreliable)} left out` : 'posts with an engagement reading'} />
            <Kpi label="Avg likes" value={waarde(posts.data.avg_likes)} icon="♥" tone="pink"
                 meta="latest reading per post" />
            <Kpi label="Avg comments" value={waarde(posts.data.avg_comments)} icon="✎" tone="teal"
                 meta="latest reading per post" />
          </div>

          <Panel title="Posts" flush
                 desc={<>Per post: new followers and new chatters from go-live <b>until the next post of that model</b> (max 24h — models post often, so longer windows would overlap other posts). Then the latest engagement reading.{posts.data.items_truncated ? ` Newest ${num(posts.data.items.length)} of ${num(posts.data.posts)} posts.` : ''}</>}>
            <Table cols={[
              { key: 'live', label: 'Went live' }, { key: 'model', label: 'Model' },
              { key: 'plat', label: 'Platform' }, { key: 'nv', label: 'New followers', num: true },
              { key: 'nc', label: 'New chatters', num: true }, { key: 'win', label: 'Window', num: true },
              { key: 'likes', label: 'Likes', num: true },
              { key: 'comments', label: 'Comments', num: true }, { key: 'views', label: 'Views (FC)', num: true },
              { key: 'bm', label: 'Bookmarks (F2F)', num: true },
            ]} emptyText="No posts went live in this window">
              {posts.data.items.map((p) => (
                <tr key={p.post_id}>
                  <td className="mono" style={{ whiteSpace: 'nowrap' }}>{alsZone(p.live_at, zone)}</td>
                  <td><ModelNaam slug={p.model_slug} /></td>
                  <td><Plat value={p.platform} /></td>
                  {p.journey ? (
                    <>
                      <td className="num mono"><b>{num(p.journey.new_followers)}</b></td>
                      <td className="num mono"><b>{num(p.journey.new_chatters)}</b></td>
                      <td className="num mono mut2">{p.journey.window_hours}h</td>
                    </>
                  ) : (
                    <td className="num" colSpan={3}><span className="dim" title="F2F before the intake fix">n/a</span></td>
                  )}
                  {p.unreliable ? (
                    <td className="num" colSpan={3}>
                      <Tag kind="bad" title="FanCentro gave the same numbers for every post">unreliable</Tag>
                    </td>
                  ) : (
                    <>
                      <td className="num mono">{waarde(p.likes)}</td>
                      <td className="num mono">{waarde(p.comments)}</td>
                      <td className="num mono">{p.platform === 'fancentro' ? waarde(p.views) : <span className="dim">n/a</span>}</td>
                    </>
                  )}
                  <td className="num mono">{p.platform === 'f2f' ? waarde(p.bookmarks) : <span className="dim">n/a</span>}</td>
                </tr>
              ))}
            </Table>
          </Panel>

          <h2 style={{ margin: '24px 0 12px' }}>Best times</h2>
          <Panel
            title="Engagement by go-live time"
            desc={`Average likes + comments per (day × hour) the post went live. Grey = no usable reading. Latest reading per post only, not a curve over time. With a short window each cell is a single day — patterns need a few weeks of data.`}
          >
            <Heat
              matrix={posts.data.heatmap.avg}
              kleur="236,72,153"
              tip={(d, h, v) => `${DAYS[d]} ${String(h).padStart(2, '0')}:00 — ${v == null
                ? 'no measured posts' : `avg ${v} (${posts.data!.heatmap.posts[d][h]} posts)`}`}
            />
          </Panel>

          <Panel title="Best times to post" flush
                 desc={`Highest average likes + comments; only slots with at least ${posts.data.min_posts_per_slot} measured posts.`}>
            {posts.data.best_slots.length === 0 ? (
              <div className="empty" style={{ padding: 16 }}>Not enough measured posts per slot yet.</div>
            ) : (
              <div className="feed">
                {posts.data.best_slots.map((s, i) => (
                  <div className="it" key={`${s.day}-${s.hour}`}>
                    <span className="rank">{i + 1}</span>
                    <span><b>{DAYS[s.day]} {String(s.hour).padStart(2, '0')}:00</b>{' '}
                      <span className="mut2">{s.posts} posts</span></span>
                    <span className="tm"><Tag kind="ok">avg {s.avg}</Tag></span>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </>
      ) : null}

      {inbound.loading ? <Loading /> : inbound.error ? <ErrorNote error={inbound.error} /> : inbound.data ? (
        <Panel title="Inbound fan messages by time"
               desc={`Messages from fans per (day × hour) — darker = more. ${num(inbound.data.total)} messages in this window.`}>
          <Heat
            matrix={inbound.data.matrix}
            kleur="99,102,241"
            tip={(d, h, v) => `${DAYS[d]} ${String(h).padStart(2, '0')}:00 — ${v ?? 0} messages`}
          />
        </Panel>
      ) : null}

      <Panel title="Coming next" desc="Not built yet — shown here so nothing looks finished that is not.">
        <div className="feed">
          <div className="it">
            <span className="rank">·</span>
            <span><b>Engagement curve over time</b>{' '}
              <span className="mut2">waits for the engagement time-series collector; now only the latest reading per post.</span></span>
            <span className="tm"><Tag kind="mut">coming</Tag></span>
          </div>
          <div className="it">
            <span className="rank">·</span>
            <span><b>FanCentro likes / comments / views</b>{' '}
              <span className="mut2">left out until FanCentro returns real numbers (now 35/30/30 for every post); comes back by itself.</span></span>
            <span className="tm"><Tag kind="mut">waiting</Tag></span>
          </div>
          <div className="it">
            <span className="rank">·</span>
            <span><b>Before / after the posting bot</b>{' '}
              <span className="mut2">dropped: this page is forward-only.</span></span>
            <span className="tm"><Tag kind="mut">dropped</Tag></span>
          </div>
        </div>
      </Panel>
    </>
  )
}
